import type { CollectionId } from '@platewell/core';
import {
  listContextQuery,
  useCollection,
  useConnectionStatus,
  useRecipeSummaries,
} from '@platewell/query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import { AddRecipeOptions } from '../components/add-recipe-options.js';
import { RecipeCard } from '../components/recipe-card.js';
import { ShareDialog } from '../components/share-dialog.js';
import { collectionRoute } from '../router.js';

export function CollectionPage() {
  const { collectionId } = collectionRoute.useParams();
  const collection = useCollection(collectionId);
  const context = { kind: 'collection', collectionId } as const;
  const recipes = useRecipeSummaries(listContextQuery(context));
  const { readOnly } = useConnectionStatus();
  const [sharing, setSharing] = useState(false);

  if (collection == null) {
    return (
      <div className="empty">
        <h2>Collection not found</h2>
        <p className="hint">It may have been deleted on another device.</p>
      </div>
    );
  }

  return (
    <>
      <div className="row wrap" style={{ marginBottom: 18 }}>
        <h1>{collection.name}</h1>
        <span className="spacer" />
        <span className="hint list-count">{recipes.length} recipes</span>
        {recipes.length > 0 && (
          <button className="btn" onClick={() => setSharing(true)}>
            Share
          </button>
        )}
        {!readOnly && (
          <>
            {/* The New Recipe page, aimed at this collection, so anything added here lands here. */}
            <Link
              className="btn"
              to="/recipes/new"
              search={{ collectionId: collectionId as CollectionId }}
            >
              Add Recipe
            </Link>
            <Link className="btn" to="/settings" search={{ tab: 'collections' }}>
              Manage
            </Link>
          </>
        )}
      </div>

      {recipes.length === 0 ? (
        <div className="editor" style={{ maxWidth: 560 }}>
          <p className="hint">
            Add your first recipes; you can easily import them by photo, file or URL. They&apos;ll be
            available on every device to every member of your household, even when you&apos;re offline.
          </p>
          <AddRecipeOptions collectionId={collectionId as CollectionId} />
        </div>
      ) : (
        <div className="grid">
          {recipes.map((recipe) => (
            <RecipeCard key={recipe.id} recipe={recipe} context={context} />
          ))}
        </div>
      )}

      {sharing && (
        <ShareDialog
          title={collection.name}
          kind="collection"
          recipeIds={recipes.map((recipe) => recipe.id)}
          onClose={() => setSharing(false)}
        />
      )}
    </>
  );
}
